import * as React from "react";
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSlotsComponentsProps,
  itIT,
  GridCellParams,
} from "@mui/x-data-grid";
import { Box, Tooltip } from "@mui/material";
import ExcelExportButton from "../shared/ExcelExportButton";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortingModel } from "../../models/shared/SortingModel";
import { FetchPaginatedApiModel } from "../../models/shared/CommonModels";
import { BeneModelDetailed } from "../../models/BeneModel";
import ErrorMessageAlertComponent from "../errors/ErrorMessageAlertComponent";
import { Constants } from "../../utils/const";
import { fetchSelectBeniDashboard } from "../../api/beni";
import DataTableFooter from "./DataTableFooter";
import { Pages } from "../../utils/pages";
import ThumbnailFoto from "../foto/ThumbnailFoto";

export interface SSPTProps {
  customer_id: string;
  contract_title: string;
  good_sub_category: string;
  good_condition: string;
  good_selling_notes: string;
  sale_date: string;
  sale_date_end: string;
}

// TODO: extract if you generalize
const condition2Color = [
  { id: "Valutazione", color: "#ffc107" },
  { id: "Approvato", color: "#4cafd3" },
  { id: "Restituito", color: "#607d8b" },
  { id: "Caricato", color: "#9e9e9e" },
  { id: "Venduto Direttamente", color: "#2196f3" },
  { id: "Reso", color: "#f44336" },
  { id: "Venduto", color: "#388e3c" },
  { id: "In Approvazione", color: "#ff9800" },
];

const colorMap = new Map();
condition2Color.forEach((data) => {
  colorMap.set(data.id, data.color);
});

// TODO: maybe generalize, for now is Bene only
const ServerSidePaginationTable: React.FC<SSPTProps> = (params) => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FetchPaginatedApiModel<BeneModelDetailed[]>>(
    undefined as any
  );
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  // Definisco columns dentro il componente per avere accesso a navigate
  const columns: GridColDef[] = [
    {
      field: "foto_principale_url",
      headerName: "Foto",
      sortable: false,
      filterable: false,
      width: 70,
      renderCell: (params) => {
        const row = params.row as BeneModelDetailed;
        return (
          <ThumbnailFoto 
            url={params.value} 
            size={50} 
            onDoubleClickPlaceholder={() => {
              navigate(`/benenew/${row.id}?openUpload=true`);
            }}
          />
        );
      },
    },
    {
      field: "customer_name",
      headerName: "Nome",
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: "customer_surname",
      headerName: "Cognome",
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: "contract_title",
      headerName: "Titolo Contratto",
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: "sotto_tipologia_bene",
      headerName: "Sotto-Categoria",
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: "brand_bene",
      headerName: "Brand",
      sortable: false,
      filterable: false,
      flex: 1,
    },
    {
      field: "bene_condizione_nome",
      headerName: "Condizione",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        const statusValue = params.value;
        return <p style={{ color: colorMap.get(statusValue) }}>{statusValue}</p>;
      },
    },
    {
      field: "data_vendita",
      headerName: "Data Vendita",
      sortable: false,
      filterable: false,
      width: 110,
    },
    {
      field: "sale_price_gross",
      headerName: "Prz. Vend.",
      sortable: false,
      filterable: false,
      width: 100,
    },
    {
      field: "sale_price_net",
      headerName: "Prz. Netto",
      sortable: false,
      filterable: false,
      width: 100,
    },
    {
      field: "selling_notes",
      headerName: "Note di Vendita",
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        const fullText = params.value as string;
        const maxLength = 20;

        const truncatedText =
          fullText && fullText.length > maxLength
            ? `${fullText.substring(0, maxLength)} ...`
            : fullText;

        return (
          <Tooltip title={fullText || ""} enterDelay={500}>
            <Box
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
              }}
            >
              {truncatedText || "-"}
            </Box>
          </Tooltip>
        );
      },
    },
  ];

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15,
  });

  // Definizione colonne per export Excel
  const excelColumns: GridColDef[] = [
    { field: "customer_name", headerName: "Nome", width: 150 },
    { field: "customer_surname", headerName: "Cognome", width: 150 },
    { field: "contract_title", headerName: "Titolo Contratto", width: 200 },
    { field: "sotto_tipologia_bene", headerName: "Sotto-Categoria", width: 150 },
    { field: "brand_bene", headerName: "Brand", width: 150 },
    { field: "bene_condizione_nome", headerName: "Condizione", width: 150 },
    { field: "data_vendita", headerName: "Data Vendita", width: 120 },
    { field: "sale_price_gross", headerName: "Prz. Vend.", width: 100 },
    { field: "sale_price_net", headerName: "Prz. Netto", width: 100 },
    { field: "selling_notes", headerName: "Note di Vendita", width: 200 },
  ];

  const excelRows = rows?.data?.map(d => ({
    customer_name: d.anagraficacliente?.personafisica?.nome || d.anagraficacliente?.personagiuridica?.ragione_sociale || "Non disponibile",
    customer_surname: d.anagraficacliente?.personafisica?.cognome || "-",
    contract_title: d.benecontratto[0].contratto?.titolo || "Non disponibile",
    sotto_tipologia_bene: d.sottotipologiabene?.nome || "Non disponibile",
    brand_bene: d.brandbene?.nome || "Non disponibile",
    bene_condizione_nome: d.benecontratto[0].condizione.nome,
    data_vendita: d.benecontratto?.[0]?.venditabenecontratto?.data_vendita
      ? new Date(d.benecontratto[0].venditabenecontratto.data_vendita).toLocaleDateString("it-IT")
      : "-",
    sale_price_gross: d.benecontratto?.[0]?.venditabenecontratto?.totale_lordo !== undefined
      ? d.benecontratto[0].venditabenecontratto.totale_lordo + "€"
      : "-",
    sale_price_net: d.benecontratto?.[0]?.venditabenecontratto?.totale_netto !== undefined
      ? d.benecontratto[0].venditabenecontratto.totale_netto + "€"
      : "-",
    selling_notes: d.benecontratto?.[0]?.venditabenecontratto?.selling_notes || "-",
  })) || [];

  const fetchGoods = useCallback(async () => {
    setLoading(true);
    try {
      const sort = {} as SortingModel;
      sort.field = "id";
      sort.direction = "DESC";
      let res = await fetchSelectBeniDashboard(
        paginationModel.page,
        params,
        sort
      );
      setRows(res.data);
      setRowCount(res.data.count);
    } catch (e: any) {
      if (e.response.data.message !== undefined)
        setErrorMessage(e.response.data.message);
    } finally {
      setLoading(false);
    }
  }, [paginationModel, params]); // TODO: verify

  // TODO: maybe use cache
  useEffect(() => {
    fetchGoods();
  }, [fetchGoods, params]); // TODO: verify

  const handleCellDoubleClick = (params: GridCellParams) => {
    const field = params.field;
    const row = params.row;
    
    // Click on Nome or Cognome -> navigate to cliente
    if (field === "customer_name" || field === "customer_surname") {
      const personaFisicaId = row.id_persona_fisica;
      const anagraficaId = row.id_cliente;
      if (personaFisicaId && anagraficaId) {
        navigate(`/clientenew/${personaFisicaId}-${anagraficaId}`);
      }
    }
    // Click on Tipo Contratto -> navigate to contratto
    else if (field === "contract_title") {
      const contractId = row.id_contratto;
      if (contractId) {
        navigate(`/contrattonew/${contractId}`);
      }
    }
    // Click on Condizione, Data Vendita, Prezzo Vendita, Prezzo Netto, Note di Vendita -> navigate to bene
    else if (
      field === "bene_condizione_nome" ||
      field === "data_vendita" ||
      field === "sale_price_gross" ||
      field === "sale_price_net" ||
      field === "selling_notes"
    ) {
      navigate(`/benenew/${row.id}`);
    }
  };

  const [errorMessage, setErrorMessage] = useState("");
  const hideError = () => setErrorMessage("");

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <ErrorMessageAlertComponent
        error_message={errorMessage}
        hide={hideError}
        visible={errorMessage.length > 0}
      />
      <DataGrid
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          display: "flex",
          flexDirection: "column-reverse",
        }}
        rows={
          rows?.data !== undefined
            ? rows.data.map((d) => {
                return {
                  id: d.id,
                  foto_principale_url: d.benecontratto[0]?.foto_principale_url,
                  id_persona_fisica: d.anagraficacliente.personafisica?.id,
                  id_cliente: d.anagraficacliente.personafisica?.id_cliente,
                  id_anagrafica: d.anagraficacliente.id,
                  id_contratto: d.benecontratto[0]?.contratto?.id,
                  customer_name: d.anagraficacliente.personafisica?.nome,
                  customer_surname: d.anagraficacliente.personafisica?.cognome,
                  contract_title:
                    d.benecontratto[0].contratto?.titolo || "Non disponibile",
                  sotto_tipologia_bene:
                    d.sottotipologiabene?.nome || "Non disponibile",
                  brand_bene: d.brandbene?.nome || "Non disponibile",
                  bene_condizione_nome: d.benecontratto[0].condizione.nome,
                  data_vendita:
                    d.benecontratto?.[0]?.venditabenecontratto?.data_vendita
                      ? new Date(d.benecontratto[0].venditabenecontratto.data_vendita).toLocaleDateString("it-IT")
                      : "-",
                  sale_price_gross:
                    d.benecontratto?.[0]?.venditabenecontratto?.totale_lordo !== undefined
                      ? d.benecontratto[0].venditabenecontratto.totale_lordo + "€"
                      : "-",
                  sale_price_net:
                    d.benecontratto?.[0]?.venditabenecontratto?.totale_netto !== undefined
                      ? d.benecontratto[0].venditabenecontratto.totale_netto + "€"
                      : "-",
                  selling_notes:
                    d.benecontratto?.[0]?.venditabenecontratto
                      ?.selling_notes !== undefined
                      ? d.benecontratto[0].venditabenecontratto.selling_notes
                      : "-",
                };
              })
            : []
        }
        columns={columns}
        rowCount={rowCount ?? 0}
        loading={loading}
        pageSizeOptions={[15]}
        autoHeight={true}
        sortingMode="server"
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        onCellDoubleClick={handleCellDoubleClick}
        disableRowSelectionOnClick
        rowHeight={Constants.TABLE_ROW_HEIGHT}
        slots={{
          footer: DataTableFooter,
        }}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              operatorInputProps: {
                disabled: true,
              },
            },
          },
          footer: {
            rows: rows,
            columns: columns,
            toolbarActions: (
              <ExcelExportButton 
                columns={excelColumns}
                rows={excelRows}
              />
            ),
          } as GridSlotsComponentsProps["footer"],
        }}
      />
    </Box>
  );
};

export default ServerSidePaginationTable;
